import styled, { css } from 'styled-components';
import { FILTERS_HEIGHT, HEADER_HEIGHT, SEARCH_WIDGET_HEIGHT } from '@/app/common/constants.ts';
import customCss from '@/app/components/hotel-detail/css/customCss.ts';
import type { Brand } from '@/domainLogic/index.ts';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import { showOnlyOnDesktopMapInSearchPage } from '@/app/common/css/mixins.ts';
import { CARD_DESKTOP_ROUNDING } from '@/app/components/search/components/EvolutionCard/constants.ts';
import { MAP_LIST_EVOLUTION, MAP_LIST_WIDTH, MAP_LIST_WIDTH_LARGE } from '../../../styles/constants';

const popupStyles = css`
  .map-overlay-popup {
    .mapboxgl-popup-tip {
      display: none;
    }

    &:not(.map-overlay-popup-mobile) {
      &:not(.map-dot-popup) {
        // bottom
        &.mapboxgl-popup-anchor-bottom {
          padding-bottom: 35px;
        }

        // top
        &.mapboxgl-popup-anchor-top {
        }

        // left
        &.mapboxgl-popup-anchor-left {
          margin-left: var(--map-clicked-marker-width);
        }

        // right
        &.mapboxgl-popup-anchor-right {
          margin-left: calc(var(--map-clicked-marker-width) * -1);
        }

        // diagonals
        &.mapboxgl-popup-anchor-top-left {
          margin-left: var(--map-clicked-marker-width);
        }
        &.mapboxgl-popup-anchor-bottom-left {
          margin-left: var(--map-clicked-marker-width);
        }
        &.mapboxgl-popup-anchor-top-right {
          margin-left: calc(var(--map-clicked-marker-width) * -1);
        }
        &.mapboxgl-popup-anchor-bottom-right {
          margin-left: calc(var(--map-clicked-marker-width) * -1);
        }
      }
    }
  }
`;

const popupStylesMobile = css`
  .map-overlay-popup-mobile {
    top: unset;
    bottom: 76px;
    z-index: 6;
    position: fixed;
    flex-direction: unset !important;
    margin-left: 50%;
    transform: translateX(-50%) !important;
    animation: animateOpacity 0.3s ease-out;
    transition:
      opacity 0.5s ease-out,
      bottom 0.3s ease-out;

    .mapboxgl-popup-tip {
      display: none;
    }
  }
`;

const airportStyles = css`
  .airport-popup {
    // is shown at the bottom
    &[style*='transform: translate(-50%, 0px)'] {
      //padding-top: 5px;
    }

    // is shown at the top
    &[style*='transform: translate(-50%, -100%)'] {
      padding-bottom: 15px;
    }

    // is shown to the left of the marker
    &[style*='transform: translate(-100%, -50%)'] {
      max-width: 245px !important;
      padding-right: 5px;
    }

    // is shown to the right of the marker
    &[style*='transform: translate(0px, -50%)'] {
      max-width: 245px !important;
      padding-left: 5px;
    }

    // is show on the bottom right corner
    &[style*='transform: translate(-100%, -100%)'] {
      max-width: 255px !important;
      padding-right: 15px;
    }

    // is show on the bottom left corner
    &[style*='transform: translate(0px, -100%)'] {
      max-width: 255px !important;
      padding-left: 15px;
    }

    .mapboxgl-popup-tip {
      visibility: hidden;
    }

    .mapboxgl-popup-content {
      box-shadow:
        0px 24px 32px rgba(0, 0, 0, 0.04),
        0px 16px 24px rgba(0, 0, 0, 0.04),
        0px 4px 8px rgba(0, 0, 0, 0.04),
        0px 0px 10px rgba(0, 0, 0, 0.04);
    }
  }
`;

const dotStyles = css`
  .marker-dot {
    min-width: 40px;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-align-items: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    border-radius: 8px;
    text-align: center;
    font-size: ${({ theme }) => theme.kallisto.font.size['175']};
    font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
    background: #fff;
    cursor: pointer;
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
    padding: 0px 8px;
    height: 28px;
    -webkit-transition: box-shadow 0.2s ease-in-out;
    transition: box-shadow 0.2s ease-in-out;
    -webkit-filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.04)) drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.06))
      drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.04));
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.04)) drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.06))
      drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.04));

    &:after {
      content: '';
      position: absolute;
      left: 50%;
      margin-left: -5px;
      border-style: solid;
      border-width: 6px 6px 0;

      top: calc(100% - 1px);
      border-color: ${({ theme }) => theme.kallisto.color.common.black[50]} transparent transparent transparent;
    }
  }
`;

const markerSelected = css`
  .openx-ui-airport-marker-selected {
    z-index: 5 !important;
  }

  .openx-ui-marker-selected {
    z-index: 6 !important;
  }

  .openx-ui-airport-marker-selected,
  .openx-ui-marker-selected {
    .marker {
      background: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      color: #fff;
      border: 0;

      .waviy span {
        color: white;
      }

      &:after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-style: solid;
        border-width: 6px 6px 0;
        border-color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault} transparent transparent
          transparent;
      }

      .marker-currency {
        color: #fff;
      }
    }

    .marker-airport-container {
      &:after {
        background: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      }
    }

    .marker-airport {
      background: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
    }
  }
`;

const markerVisited = css`
  .openx-ui-marker-visited {
    z-index: 0;

    .marker {
      color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
      background: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};
      border: 1px solid ${({ theme }) => theme.kallisto.color.text.static.textSecondary};

      &:before {
        content: '';
        position: absolute;
        top: calc(100% + 0px);
        left: 50%;
        margin-left: -5px;
        border-style: solid;
        border-width: 6px 6px 0;
        border-color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary} transparent transparent
          transparent;
      }

      &:after {
        content: '';
        position: absolute;
        left: 50%;
        margin-left: -5px;
        border-style: solid;
        border-width: 6px 6px 0;

        top: calc(100% - 1px);
        border-color: ${({ theme }) => theme.kallisto.color.common.black[50]} transparent transparent transparent;
      }

      .marker-currency {
        color: #8c8c92;
      }
    }
  }
`;

// The zoom glyphs are rasters requested at twice this, so the size has to be set here: Mapbox
// leaves the icon at its intrinsic size, which would draw the retina copy twice as big.
export const ZOOM_ICON_SIZE = 24;

const mapControlsStyles = css<{
  $zoomInIcon: string;
  $zoomOutIcon: string;
  $isDialog?: boolean;
}>`
  &.map-container-evolution {
    .mapboxgl-ctrl {
      &-top-right {
        .mapboxgl-ctrl-group {
          margin-top: 72px;

          /* MPH-981 (Figma 979-41667): in the detail dialog the zoom sits 16px under the close button. */
          ${({ $isDialog }) =>
            $isDialog &&
            css`
              @media (min-width: 1024px) {
                margin-top: 80px;
              }
            `}
        }
      }
    }

    /* MPH-981: the detail dialog keeps the credits in the bottom-left corner, flush with the edges like the
       results map's bottom-right strip. The hidden logo's empty wrapper there (Mapbox shows it inline, so
       only its margin can go) lifted them 10px, and the dialog body's 24px line-height made the strip 26px
       tall where the results page inherits a unitless 1. The full strip also clears the desktop panel's
       16px rounded corner, which clipped its first glyph. */
    ${({ $isDialog }) =>
      $isDialog &&
      css`
        .mapboxgl-ctrl-bottom-left {
          .mapboxgl-ctrl:has(> .mapboxgl-ctrl-logo) {
            margin: 0;
          }

          .mapboxgl-ctrl-attrib {
            line-height: 1;

            &:not(.mapboxgl-compact) {
              padding-left: 12px;
            }
          }
        }
      `}
  }
  .mapboxgl-ctrl {
    box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.mediumDownShadow};

    &-group {
      border-radius: 8px;
    }

    &-top-right {
      @media (max-width: 1023px) {
        display: none;
      }

      .mapboxgl-ctrl-group {
        margin-top: 24px;
        margin-right: 24px;
      }
    }

    &-zoom-in {
      width: 40px;
      height: 40px;

      &:hover {
        box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow} !important;
        background-color: white !important;
        border-radius: 8px;
      }

      .mapboxgl-ctrl-icon {
        background-image: url(${({ $zoomInIcon }) => $zoomInIcon}) !important;
        background-size: ${ZOOM_ICON_SIZE}px ${ZOOM_ICON_SIZE}px;
      }
    }

    &-zoom-out {
      width: 40px;
      height: 40px;
      border-top: unset;

      &:hover {
        box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow} !important;
        background-color: white !important;
        border-radius: 8px;
      }

      .mapboxgl-ctrl-icon {
        background-image: url(${({ $zoomOutIcon }) => $zoomOutIcon}) !important;
        background-size: ${ZOOM_ICON_SIZE}px ${ZOOM_ICON_SIZE}px;
      }
    }

    &-attrib-inner * {
      font-size: 12px;
    }
  }
`;

const skiliftStyles = css`
  .skilift-popup {
    .mapboxgl-popup-content {
      padding: 16px;
      font-size: ${({ theme }) => theme.kallisto.font.size['175']};
      font-weight: 700;
      line-height: 20px;
      span {
        color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
        font-weight: 400;
        line-height: 20px;
      }
    }
    .mapboxgl-popup-tip {
      display: none;
    }
  }
`;

export const MapContainerStyled = styled.div<{
  ref: React.MutableRefObject<HTMLDivElement | undefined>;
  $expand: boolean;
  $zoomInIcon: string;
  $zoomOutIcon: string;
  $isMapOnTop: boolean;
  layoutType: LAYOUT_TYPE;
  $forceFullscreenMapDesktop: boolean | undefined;
  $externalHeaderEnabled: boolean;
  $brand: Brand;
  evolutionResultsOnlyEnabled: boolean;
  $isDialog?: boolean;
}>`
  position: fixed;
  right: 0;
  display: block;
  background: #eaeaeb;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 0;

  ${showOnlyOnDesktopMapInSearchPage};

  ${({ layoutType, $externalHeaderEnabled, $brand, evolutionResultsOnlyEnabled }) =>
    layoutType === LAYOUT_TYPE.DESKTOP_MAP &&
    css`
      @media (min-width: 1024px) {
        bottom: 0;
        z-index: 1;
        left: ${evolutionResultsOnlyEnabled ? MAP_LIST_EVOLUTION : MAP_LIST_WIDTH};

        ${
          $externalHeaderEnabled
            ? `
          @media ${customCss.breakpoints.mdPlus} {
            top: ${SEARCH_WIDGET_HEIGHT + HEADER_HEIGHT($brand, false) + FILTERS_HEIGHT}px;
          }
          `
            : `top: 134px;`
        }
      }

      @media (min-width: 1280px) {
        left: ${evolutionResultsOnlyEnabled ? MAP_LIST_EVOLUTION : MAP_LIST_WIDTH_LARGE};
      }
    `};

  @media (max-width: 1023px) {
    bottom: 0;
  }

  ${({ $forceFullscreenMapDesktop }) =>
    $forceFullscreenMapDesktop &&
    css`
      top: 56px;

      @media (min-width: 1024px) {
        left: 0;
        top: 56px;
        right: 0 !important;
        bottom: 0;
      }
    `};

  @media (min-width: 1025px) {
    transition: left 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  }

  @media (min-width: 1024px) {
    ${({ $expand }) =>
      $expand &&
      css`
        left: 0;
      `}

    ${({ $isMapOnTop }) => css`
      z-index: ${$isMapOnTop ? 2 : 0};
    `}
  }

  .mapboxgl-popup {
    z-index: 5;
  }

  .mapboxgl-popup-content {
    padding: 0;
    border-radius: 8px;
    max-width: 100%;

    ${({ evolutionResultsOnlyEnabled }) =>
      evolutionResultsOnlyEnabled &&
      css`
        border-radius: ${CARD_DESKTOP_ROUNDING};
      `};
  }

  .mapboxgl-ctrl-top-right {
    z-index: 6;
  }

  .mapboxgl-map {
    min-height: 100%;
  }

  .mapboxgl-ctrl-bottom-left {
    a.mapboxgl-ctrl-logo {
      display: none;
    }

    div.mapboxgl-ctrl.mapboxgl-ctrl-scale {
      display: none;
      @media (max-width: 1024px) {
        display: block;
      }
    }
  }

  .mapboxgl-ctrl-bottom-right {
    div.mapboxgl-ctrl.mapboxgl-ctrl-scale {
      display: none;
      @media (min-width: 1025px) {
        display: block;
      }
    }
  }

  .mapboxgl-marker {
    z-index: 0;
    &:hover {
      z-index: 5 !important;
    }
  }

  ${popupStyles};
  ${popupStylesMobile};
  ${airportStyles};
  ${mapControlsStyles};
  ${skiliftStyles};
  ${dotStyles};
  ${markerVisited};
  ${markerSelected};
`;
